/**
 * RTK Query API slice for the real Virtual Library backend.
 * Maps to all 18 endpoints in LibraryApiController (api/library/*).
 *
 * Base URL: {bootConfig.libraryApiBaseUrl}/library
 * e.g. in production: /api/library
 */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import bootConfig from 'utils/bootConfig';
import type {
  NavCategory,
  NavCategoriesParams,
  NavResource,
  NavResourcesParams,
  SearchResult,
  SearchParams,
  UserPermissions,
  CategoryDetail,
  ResourceDetail,
  LocationNode,
  LocationTreeNode,
  LocationTreeChildrenParams,
  ParentCategoriesParams,
  ParentCategoryTreeNode,
  ParentCategoryChildrenParams,
  RoleLocation,
  ResourceStatistics,
  CreateLibraryCategoryRequest,
  UpdateLibraryCategoryRequest,
  CreateLibraryResourceRequest,
  UpdateLibraryResourceRequest,
  CategoryCreateInitResponse,
  CategoryEditInitResponse,
  CategorySelectorItem,
  ExcludedLocation,
  ResourceEditInitResponse,
  ResourceCreateInitResponse,
  ResourceExclusionHierarchyVerifyResponse,
  CategoryExclusionHierarchyVerifyResponse,
} from 'types/LibraryApi';


export const libraryApi = createApi({
  reducerPath: 'libraryApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${bootConfig.libraryApiBaseUrl}/library`,
  }),
  tagTypes: ['Category', 'Resource', 'Permissions'],
  endpoints: (builder) => ({

    // ── API 01 — GET /categories ──────────────────────────────────────────
    getNavCategories: builder.query<NavCategory[], NavCategoriesParams>({
      query: ({ lineage, isManageMode }) => ({
        url: 'categories',
        params: { lineage, isManageMode },
      }),
      providesTags: ['Category'],
    }),

    // ── API 02 — GET /resources ───────────────────────────────────────────
    getNavResources: builder.query<NavResource[], NavResourcesParams>({
      query: ({ lineage, category, isManageMode }) => ({
        url: 'resources',
        params: { lineage, category, isManageMode },
      }),
      providesTags: ['Resource'],
    }),

    // ── API 03 — GET /search ──────────────────────────────────────────────
    // searchIn is sent as a single comma-separated query-string value
    // (e.g. ?searchIn=categoryName,resourceName). The backend splits on commas;
    // URLSearchParams.set() with a joined string guarantees correct serialisation.
    searchLibrary: builder.query<SearchResult[], SearchParams>({
      query: ({ q, searchType, searchIn = [], isManageMode }) => {
        const qs = new URLSearchParams();
        qs.set('q', q);
        qs.set('searchType', searchType);
        qs.set('isManageMode', String(isManageMode));
        if (searchIn.length > 0) qs.set('searchIn', searchIn.join(','));
        return { url: `search?${qs.toString()}` };
      },
      // Keep search results fresh after any create/edit/delete of a category/resource.
      providesTags: ['Category', 'Resource'],
    }),

    // ── API 04 — GET /resourceAccess/{id} ────────────────────────────────
    getResourceAccess: builder.query<{ canAccess: boolean }, number>({
      query: (id) => `resourceAccess/${id}`,
    }),

    // ── API 05 — GET /categoryAccess ─────────────────────────────────────
    getCategoryAccess: builder.query<{ canAccess: boolean }, { lineage: string; category: string }>({
      query: ({ lineage, category }) => ({
        url: 'categoryAccess',
        params: { lineage, category },
      }),
    }),

    // ── API 06 — GET /permissions ─────────────────────────────────────────
    getPermissions: builder.query<UserPermissions, void>({
      query: () => 'permissions',
      providesTags: ['Permissions'],
    }),

    // ── API 07 — GET /categories/{id} ────────────────────────────────────
    getCategoryById: builder.query<CategoryDetail, number>({
      query: (id) => `categories/${id}`,
      providesTags: (_, __, id) => [{ type: 'Category', id }],
    }),

    // ── API 08 — GET /resources/{id} ─────────────────────────────────────
    getResourceById: builder.query<ResourceDetail, number>({
      query: (id) => `resources/${id}`,
      providesTags: (_, __, id) => [{ type: 'Resource', id }],
    }),

    // ── API 09 — GET /locations ───────────────────────────────────────────
    // permission defaults to LIBRARY_MANAGE on the server when omitted.
    getLocationTree: builder.query<LocationNode[], string | void>({
      query: (permission) => ({
        url: 'locations',
        params: permission ? { permission } : undefined,
      }),
    }),

    // ── API 09b — GET /category-create-init ──────────────────────────────
    // Returns idLocation, idCategory, locationTree, parentCategoryTree.
    // Call with the lineage where the category is being created/edited.
    getCategoryCreateInit: builder.query<CategoryCreateInitResponse, string>({
      query: (lineage) => ({
        url: 'category-create-init',
        params: { lineage },
      }),
      // Drop the cache when the Create modal closes so reopening always fetches a
      // fresh location/parent tree — e.g. a folder just created shows up as a
      // selectable parent instead of being missing until the cache expires.
      keepUnusedDataFor: 0,
    }),

    // ── API 09b2 — GET /resource-create-init ─────────────────────────────
    // Returns idLocation, idCategory, locationTree, parentCategoryTree.
    // Call with the lineage where the resource is being created.
    getResourceCreateInit: builder.query<ResourceCreateInitResponse, string>({
      query: (lineage) => ({
        url: 'resource-create-init',
        params: { lineage },
      }),
      // Drop the cache when the Create modal closes so reopening always fetches a
      // fresh location/folder tree (a just-created folder appears as a target).
      keepUnusedDataFor: 0,
    }),

    // ── API 09c — GET /category-edit-init ────────────────────────────────
    // Returns location options for the given category (used by Delete/Edit Category page).
    // Pass the optional `idCategory` to re-scope the response to a specific
    // category instance when the user picks a different "Which Location?" option.
    getCategoryEditInit: builder.query<CategoryEditInitResponse, { editLineage: string; editCategory: string; idCategory?: number }>({
      query: ({ editLineage, editCategory, idCategory }) => ({
        url: 'category-edit-init',
        params: { editLineage, editCategory, ...(idCategory != null ? { idCategory } : {}) },
      }),
      // Intentionally provides NO tags. This init data is refetched on every
      // mount (refetchOnMountOrArgChange in the hook), so it is always fresh when
      // the Edit modal opens. Providing 'Category' here would make the UPDATE
      // mutation re-run this query — keyed on the OLD category name — after a
      // rename, producing a spurious 404 while the modal is closing.
      // keepUnusedDataFor: 0 drops the cache on close so a reopen never serves the
      // stale pre-save location/parent on its first render before the refetch lands.
      keepUnusedDataFor: 0,
    }),

    // ── API 09e — GET /category-delete-init ──────────────────────────────
    // Returns location selector items for the Delete Category page.
    getCategoryDeleteInit: builder.query<CategorySelectorItem[], { editLineage: string; editCategory: string }>({
      query: ({ editLineage, editCategory }) => ({
        url: 'category-delete-init',
        params: { editLineage, editCategory },
      }),
      // Intentionally provides NO tags. This init data is refetched on every
      // mount (refetchOnMountOrArgChange in the hook), so it is always fresh when
      // the modal opens. Providing Category/Resource here would make the DELETE
      // mutation re-run this query against the just-deleted id → a spurious 404.
      // keepUnusedDataFor: 0 drops the cache on close so a reopen never briefly
      // shows stale child counts before the refetch lands.
      keepUnusedDataFor: 0,
    }),

    // ── API 09d — GET /location-tree-children ────────────────────────────
    // Lazy-loads children of a location node. Returns LocationTreeNode[].
    // Call with idParent from the parent node. Empty array = leaf node.
    getLocationTreeChildren: builder.query<LocationTreeNode[], LocationTreeChildrenParams>({
      query: ({ isCreateMode, idParent }) => ({
        url: 'location-tree-children',
        params: { isCreateMode, idParent },
      }),
    }),

    // ── API 09f — GET /excluded-location-tree ────────────────────────────
    // Returns the excluded-location tree scoped to a top-level location.
    getExcludedLocationTree: builder.query<LocationTreeNode[], { idTopLocation: number; isCreateMode?: boolean }>({
      query: ({ idTopLocation, isCreateMode = true }) => ({
        url: 'excluded-location-tree',
        params: { idTopLocation, isCreateMode },
      }),
    }),

    // ── API 09g — GET /category-excluded-locations ───────────────────────
    // Returns previously-saved excluded locations for a given category.
    getCategoryExcludedLocations: builder.query<ExcludedLocation[], { idCategory: number }>({
      query: ({ idCategory }) => ({
        url: 'category-excluded-locations',
        params: { idCategory },
      }),
      // Drop the cache as soon as the Edit modal closes (no subscribers). Without
      // this, a reopen serves the stale pre-save list on its FIRST render (RTK
      // returns cached data with isFetching:false before refetchOnMountOrArgChange
      // dispatches the refetch), and the one-shot populate effect locks it in — so
      // the change only appears on a SECOND reopen. With no cache, the reopen has
      // nothing stale to show and populates from the fresh fetch.
      keepUnusedDataFor: 0,
    }),

    // ── API 09h — GET /excluded-location-search ──────────────────────────
    // Search excluded locations for categories.
    searchExcludedLocations: builder.query<LocationTreeNode[], { idTopLocation: number; isCreateMode: boolean; searchText: string; idLocationType?: string; includeSubLocations: boolean }>({
      query: ({ idTopLocation, isCreateMode, searchText, idLocationType, includeSubLocations }) => ({
        url: 'excluded-location-search',
        params: { idTopLocation, isCreateMode, searchText, ...(idLocationType ? { idLocationType } : {}), includeSubLocations },
      }),
    }),

    // ── API 09i — GET /resource-excluded-location-search ─────────────────
    // Search excluded locations for resources.
    searchResourceExcludedLocations: builder.query<LocationTreeNode[], { idTopLocation: number; isCreateMode: boolean; searchText: string; idLocationType?: string; includeSubLocations: boolean }>({
      query: ({ idTopLocation, isCreateMode, searchText, idLocationType, includeSubLocations }) => ({
        url: 'resource-excluded-location-search',
        params: { idTopLocation, isCreateMode, searchText, ...(idLocationType ? { idLocationType } : {}), includeSubLocations },
      }),
    }),

    // ── API 10 — GET /parent-categories?idLocation= ────────────────────────────
    getParentCategoriesByLocation: builder.query<ParentCategoryTreeNode[], ParentCategoriesParams>({
      query: ({ idLocation }) => ({
        url: 'parent-categories',
        params: { idLocation },
      }),
      // Intentionally provides NO tags. The admin forms fetch this on demand
      // via a lazy trigger (fetchCategoriesForLocation) whenever a location is
      // picked, so it is always fresh. Providing 'Category' would make a
      // create/edit/delete mutation re-run this query after the form saves.
    }),

    // ── API 10b — GET /parent-category-children?idParent= ────────────────────
    // Lazy-loads children of a parent category node. Returns ParentCategoryTreeNode[].
    getParentCategoryChildren: builder.query<ParentCategoryTreeNode[], ParentCategoryChildrenParams>({
      query: ({ idParent }) => ({
        url: 'parent-category-children',
        params: { idParent },
      }),
    }),

    // ── API 10 (legacy) — GET /parentCategories?lineage= ─────────────────────
    getParentCategories: builder.query<{ id: number; name: string }[], { lineage: string; isManageMode?: boolean }>({
      query: ({ lineage, isManageMode = true }) => ({
        url: 'parentCategories',
        params: { lineage, isManageMode },
      }),
      providesTags: ['Category'],
    }),

    // ── API 19 — GET /resource-edit-init?idResource= ──────────────────────
    // Returns all data to pre-populate the Edit Resource page in one call.
    getResourceEditInit: builder.query<ResourceEditInitResponse, { idResource: number }>({
      query: ({ idResource }) => ({
        url: 'resource-edit-init',
        params: { idResource },
      }),
      // Intentionally provides NO tags. This init data is refetched on every
      // mount (refetchOnMountOrArgChange in the hook), so it is always fresh when
      // the Edit modal opens. Providing 'Resource' here would make the UPDATE
      // mutation re-run this query after the form saves (an unnecessary refetch
      // of a resource whose modal is already closing).
      // keepUnusedDataFor: 0 drops the cache on close so a reopen never serves the
      // stale pre-save resource on its first render before the refetch lands.
      keepUnusedDataFor: 0,
    }),

    // ── API 11 — GET /resource-role-location-options ─────────────────────
    getRoleLocations: builder.query<RoleLocation[], { idLocation: number }>({
      query: ({ idLocation }) => ({
        url: 'resource-role-location-options',
        params: { idLocation },
      }),
    }),

    // ── GET /resource-exclusion-hierarchy-verify ──────────────────────────
    // Verifies that a candidate location is at or above all excluded locations
    // for the given resource. Call before allowing a location change when the
    // resource has existing excluded locations.
    getResourceExclusionHierarchyVerify: builder.query<
      ResourceExclusionHierarchyVerifyResponse,
      { idLocation: number; idResource: number }
    >({
      query: ({ idLocation, idResource }) => ({
        url: 'resource-exclusion-hierarchy-verify',
        params: { idLocation, idResource },
      }),
    }),

    // ── GET /category-exclusion-hierarchy-verify ──────────────────────────
    // Same as the resource variant but scoped to a category.
    getCategoryExclusionHierarchyVerify: builder.query<
      CategoryExclusionHierarchyVerifyResponse,
      { idLocation: number; idCategory: number }
    >({
      query: ({ idLocation, idCategory }) => ({
        url: 'category-exclusion-hierarchy-verify',
        params: { idLocation, idCategory },
      }),
    }),

    // ── API 12 — GET /resource-statistics?idResource={id} ────────────────
    getResourceStatistics: builder.query<ResourceStatistics, number>({
      query: (id) => ({
        url: 'resource-statistics',
        params: { idResource: id },
      }),
      // Tag per-resource so editing a resource (updateLibraryResource /
      // updateLibraryResourceFile invalidate { type: 'Resource', id }) refetches
      // its statistics automatically — otherwise the details panel keeps showing
      // the pre-edit "Last Modified" / hit counts from cache until a hard refresh.
      // Safe to tag (unlike the name-keyed *-edit-init queries that 404 on rename):
      // this is keyed on the STABLE numeric idResource, so an edit never makes it
      // refetch a no-longer-existing key.
      providesTags: (_, __, id) => [{ type: 'Resource', id }],
      // Drop the cached stats the moment the details panel closes (the query
      // unsubscribes via `skip`). A resource's hit counts change every time it's
      // opened, and opening the panel is exactly when we want the LATEST numbers,
      // so we must never serve a stale cached result on the next open. With no
      // surviving cache entry, every reopen refetches fresh instead of reading
      // from state.
      keepUnusedDataFor: 0,
    }),

    // ── API 13 — POST /categories ─────────────────────────────────────────
    createLibraryCategory: builder.mutation<{ id: number }, CreateLibraryCategoryRequest>({
      query: (body) => ({ url: 'categories', method: 'POST', body }),
      invalidatesTags: ['Category'],
    }),

    // ── API 14 — PUT /categories/{id} ────────────────────────────────────
    updateLibraryCategory: builder.mutation<{ id: number }, { id: number } & UpdateLibraryCategoryRequest>({
      query: ({ id, ...body }) => ({ url: `categories/${id}`, method: 'PUT', body }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Category', id }, 'Category'],
    }),

    // ── API 15 — DELETE /categories/{id} ─────────────────────────────────
    deleteLibraryCategory: builder.mutation<void, number>({
      query: (id) => ({ url: `categories/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Category', 'Resource'],
    }),

    // ── API 16 — POST /resources (JSON — URL resources) ──────────────────
    createLibraryResource: builder.mutation<{ id: number }, CreateLibraryResourceRequest>({
      query: (body) => ({ url: 'resources', method: 'POST', body }),
      invalidatesTags: ['Resource'],
    }),

    // ── API 16 — POST /resources/upload (multipart — file upload) ───────────
    // Build FormData with individual fields (name, description, idcategory,
    // idlocation, iscascading, ispublic, rolelocationid, excludedlocations, file).
    // Field names must be lowercase — the backend matches via ToLowerInvariant().
    createLibraryResourceFile: builder.mutation<{ id: number }, FormData>({
      query: (formData) => ({
        url: 'resources/upload',
        method: 'POST',
        body: formData,
        // Do NOT set Content-Type — the browser sets multipart boundary automatically
        formData: true,
      }),
      invalidatesTags: ['Resource'],
    }),

    // ── API 17 — PUT /resources/{id} (JSON) ──────────────────────────────
    updateLibraryResource: builder.mutation<{ id: number }, { id: number } & UpdateLibraryResourceRequest>({
      query: ({ id, ...body }) => ({ url: `resources/${id}`, method: 'PUT', body }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Resource', id }, 'Resource'],
    }),

    // ── API 17 — PUT /resources/{id}/upload (multipart — file replacement) ─────
    updateLibraryResourceFile: builder.mutation<{ id: number }, { id: number; formData: FormData }>({
      query: ({ id, formData }) => ({
        url: `resources/${id}/upload`,
        method: 'PUT',
        body: formData,
        // Do NOT set Content-Type — the browser sets multipart boundary automatically
        formData: true,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Resource', id }, 'Resource'],
    }),

    // ── API 18 — DELETE /resources/{id} ──────────────────────────────────
    // Invalidates Category as well as Resource so folder item counts refresh.
    // Both tags are invalidated by this ONE mutation, so a query that provides
    // both (e.g. searchLibrary) still refetches only once. Do NOT also dispatch
    // a manual invalidateTags in the page delete handler — doing so invalidated
    // 'Resource' a second time and made the resources endpoint fire twice.
    deleteLibraryResource: builder.mutation<void, number>({
      query: (id) => ({ url: `resources/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Resource', 'Category'],
    }),
  }),
});

export const {
  useGetNavCategoriesQuery,
  useGetNavResourcesQuery,
  useSearchLibraryQuery,
  useLazySearchLibraryQuery,
  useGetResourceAccessQuery,
  useGetCategoryAccessQuery,
  useGetPermissionsQuery,
  useGetCategoryByIdQuery,
  useGetResourceByIdQuery,
  useGetLocationTreeQuery,
  useGetCategoryCreateInitQuery,
  useGetResourceCreateInitQuery,
  useGetCategoryEditInitQuery,
  useGetCategoryDeleteInitQuery,
  useGetLocationTreeChildrenQuery,
  useLazyGetLocationTreeChildrenQuery,
  useLazyGetExcludedLocationTreeQuery,
  useGetCategoryExcludedLocationsQuery,
  useLazySearchExcludedLocationsQuery,
  useLazySearchResourceExcludedLocationsQuery,
  useGetParentCategoriesQuery,
  useGetParentCategoriesByLocationQuery,
  useLazyGetParentCategoriesByLocationQuery,
  useGetParentCategoryChildrenQuery,
  useLazyGetParentCategoryChildrenQuery,
  useGetRoleLocationsQuery,
  useLazyGetResourceExclusionHierarchyVerifyQuery,
  useLazyGetCategoryExclusionHierarchyVerifyQuery,
  useGetResourceEditInitQuery,
  useGetResourceStatisticsQuery,
  useCreateLibraryCategoryMutation,
  useUpdateLibraryCategoryMutation,
  useDeleteLibraryCategoryMutation,
  useCreateLibraryResourceMutation,
  useCreateLibraryResourceFileMutation,
  useUpdateLibraryResourceMutation,
  useUpdateLibraryResourceFileMutation,
  useDeleteLibraryResourceMutation,
} = libraryApi;
